import Axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { MARKETPLACE_CONFIG, getAppConfig } from '@com/core/common/utils/config';
import getFingerprint, { getVisitorFingerprint } from '../utils/fingerprint';
import { reportEvent } from '../utils/sentry';
import { IS_BROWSER } from '../baseConfig';
import country, { TCountryEndpoints } from './country';
import city, { TCityEndpoints } from './city';
import webconfig, { TWebconfigEndpoints } from './webconfig';
import customer, { TCustomerEndpoints } from './customer';
import cart, { TCartEndpoints } from './cart';
import cartDaily, { TCartDailyEndpoints } from './cartDaily';
import catalog, { internalCatalog, TCatalogEndpoints, TInternalCatalogEndpoints } from './catalog';
import ofd, { TOfdEndpoints } from './ofd';
import recommendation, { TRecommendationEndpoints } from './recommendation';
import store, { TStoreEndpoints } from './store';
import noonReview, { TReviewEndpoint } from './noonReview';
import otherReview, { TOtherReviewEndpoints } from './otherReview';
import subscriptions, { TSubscriptionEndpoints } from './subscriptions';
import { TLocaleAPI } from '../types/locale';
import ads, { TAdEndpoints } from './ads';

// Enable reporting only in dev
const IS_DEV = process.env.ENV !== 'prod';

// Common headers for all calls
const commonHeaders = {
    ...MARKETPLACE_CONFIG.commonHeaders,
    'Cache-Control': 'no-cache, max-age=0, must-revalidate, no-store',
    // Move this to baseConfig as well.
};

// #region AXIOS INSTANCES (4)

// (1): Usual calls from client
const api: AxiosInstance = Axios.create({
    // Requests are sent using a reverse proxy, check server.ts
    baseURL: '/',
    headers: {
        ...commonHeaders,
    },
});

// (2): Internal fetch for webconfig (happens on SSR)
const apiInternal: AxiosInstance = Axios.create({
    baseURL: MARKETPLACE_CONFIG.endpoints.webconfig,
    headers: {
        ...commonHeaders,
    },
});

// (3): Internal fetch for catalog (happens on SSR)
const apiInternalCatalog: AxiosInstance = Axios.create({
    baseURL: `${MARKETPLACE_CONFIG.endpoints.catalog}/u`,
    headers: {
        ...commonHeaders,
    },
});

// (4): External fetch for reviews (happens on client only)
const apiReviewsExternal: AxiosInstance = Axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
});
// #endregion AXIOS INSTANCES (4)

// #region INTERCEPTORS: Requests
api.interceptors.request.use(
    async config => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.info('✉️ ', config);
        }

        const appConfig = await getAppConfig();

        const updatedConfig = {
            ...config,
            headers: {
                ...config.headers,
                ...appConfig.commonHeaders,
            },
        };

        // Attach fingerprint, according to marketplace
        if (appConfig.enabledFeatures.fingerprinting) {
            let fingerprint = IS_BROWSER ? 'sus' : 'server';

            const t0 = performance.now();

            if (IS_BROWSER) {
                try {
                    if (config.method === 'post') {
                        const endpoint = config.url.split('/').pop();
                        if (endpoint === 'signin' || endpoint === 'customer') {
                            fingerprint = await getFingerprint();
                        }
                    } else {
                        fingerprint = await getVisitorFingerprint();
                    }
                } catch (err) {
                    reportEvent('Could not get fingerprint', 'API Interceptor', 'warning', { err });
                }
            }

            // Silenty warn of slow performance
            const t1 = performance.now();
            try {
                if (t1 - t0 > 5000) {
                    reportEvent('Slow fingerprint performance', 'API Interceptor', 'warning', {
                        t1,
                        t0,
                        time: t1 - t0,
                    });
                }
            } catch (err) {
                reportEvent('Could not calculate performance', 'API Interceptor', 'warning', { err });
            }
            updatedConfig.headers['x-authorization'] = `Bearer ${fingerprint}`;
        }

        return updatedConfig;
    },
    error => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('✉️ ', error);
        }

        return Promise.reject(error);
    },
);

apiInternal.interceptors.request.use(
    async config => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.info('✉️ ', config);
        }

        const appConfig = await getAppConfig();

        return {
            ...config,
            headers: {
                ...config.headers,
                ...appConfig.commonHeaders,
            },
        };
    },
    error => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('✉️ ', error);
        }

        return Promise.reject(error);
    },
);

apiInternalCatalog.interceptors.request.use(
    async config => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.info('✉️ ', config);
        }

        const appConfig = await getAppConfig();

        return {
            ...config,
            headers: {
                ...config.headers,
                ...appConfig.commonHeaders,
            },
        };
    },
    error => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('✉️ ', error);
        }

        return Promise.reject(error);
    },
);
// #endregion INTERCEPTORS: Requests

// #region INTERCEPTORS: Responses

api.interceptors.response.use(
    (response: AxiosResponse) => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.info('📩 ', response);
        }

        return response;
    },
    (error: AxiosError) => {
        const genericError = 'Something went wrong.';
        const offlineError = 'Looks like you’re offline. Make sure you’re connected to the internet and try again';

        /**
         * API sends an error message in the format
         * { error: error_message }
         */
        if (error.response && error.response.data) {
            /**
             * If the error is NOT initiated from the API (ie: for express/cloudflare errors)
             * then use generic error message for toast message and pass error info to sentry
             */
            if (typeof error.response.data === 'string') {
                const newData: {
                    error: string;
                    info: string;
                    status: number;
                } = {
                    error: genericError,
                    info: error.response.data,
                    status: error.response.status,
                };

                return Promise.reject(newData);
            }

            if (IS_DEV && IS_BROWSER) {
                // eslint-disable-next-line no-console
                console.error('Error: ', error.response.data);
            }

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject({ ...error.response.data, status: error.response.status });
        }
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('📩 ', error);
        }

        if (error.message === 'Network Error') {
            // #TODO
            // eslint-disable-next-line no-param-reassign
            error.message = offlineError;
        }

        // #TODO: this needs some enhancing
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: error.message, status: 418 });
    },
);

apiInternal.interceptors.response.use(
    async config => {
        // if (IS_DEV && IS_BROWSER) {
        //     // eslint-disable-next-line no-console
        //     console.error('✉️ ', config);
        // }
        return config;
    },
    error => {
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('✉️ ', error);
        }

        return Promise.reject(error);
    },
);

apiInternalCatalog.interceptors.response.use(
    (response: AxiosResponse) => {
        // For debugging
        // if (IS_DEV) {
        //     console.info('📩 ', response);
        // }

        return response;
    },
    (error: AxiosError) => {
        const genericError = 'Something went wrong.';
        const offlineError = 'Looks like you’re offline. Make sure you’re connected to the internet and try again';

        /**
         * API sends an error message in the format
         * { error: error_message }
         */
        if (error?.response?.data) {
            /**
             * If the error is NOT initiated from the API (ie: for express/cloudflare errors)
             * then use generic error message for toast message and pass error info to sentry
             */
            if (typeof error.response.data === 'string') {
                const newData: {
                    error: string;
                    info: string;
                    status: number;
                } = {
                    error: genericError,
                    info: error.response.data,
                    status: error.response.status,
                };

                return Promise.reject(newData);
            }

            if (IS_DEV && IS_BROWSER) {
                // eslint-disable-next-line no-console
                console.error('Error: ', error.response.data);
            }

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject({ ...error.response.data, status: error.response.status });
        }
        if (IS_DEV && IS_BROWSER) {
            // eslint-disable-next-line no-console
            console.error('📩 ', error);
        }

        if (error.message === 'Network Error') {
            // #TODO
            // eslint-disable-next-line no-param-reassign
            error.message = offlineError;
        }

        // #TODO: this needs some enhancing
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: error.message, status: 418 });
    },
);
// #endregion INTERCEPTORS: Responses

// --- --- --- --- --- --- SERVICES --- --- --- --- --- ---
export const countrySVC = (apiLocale: TLocaleAPI): TCountryEndpoints => country(api, apiLocale);
export const citySVC = (apiLocale: TLocaleAPI): TCityEndpoints => city(api, apiLocale);
export const webConfigSVC = (apiLocale: TLocaleAPI): TWebconfigEndpoints => webconfig(apiInternal, apiLocale);
export const customerSVC = (apiLocale: TLocaleAPI): TCustomerEndpoints => customer(api, apiLocale);
export const cartSVC = (apiLocale: TLocaleAPI): TCartEndpoints => cart(api, apiLocale);
export const cartDailySVC = (apiLocale: TLocaleAPI): TCartDailyEndpoints => cartDaily(api, apiLocale);
export const catalogSVC = (apiLocale: TLocaleAPI): TCatalogEndpoints => catalog(api, apiLocale);
export const internalCatalogSVC = (apiLocale: TLocaleAPI): TInternalCatalogEndpoints =>
    internalCatalog(apiInternalCatalog, apiLocale);
export const noonReviewSVC = (apiLocale: TLocaleAPI): TReviewEndpoint => noonReview(api, apiLocale);
export const otherReviewSVC = (apiLocale: TLocaleAPI): TOtherReviewEndpoints =>
    otherReview(apiReviewsExternal, apiLocale);
export const recommendationSVC = (apiLocale: TLocaleAPI): TRecommendationEndpoints => recommendation(api, apiLocale);
// export const review = (apiLocale: TLocaleAPI): TCatalogReviewEndpoints => catalogReview(apiReviewsExternal, apiLocale);
export const ofdSVC = (apiLocale: TLocaleAPI): TOfdEndpoints => ofd(api, apiLocale);
export const storeSVC = (apiLocale: TLocaleAPI): TStoreEndpoints => store(api, apiLocale);
export const adSVC = (apiLocale: TLocaleAPI): TAdEndpoints => ads(api, apiLocale);
export const subscriptionsSVC = (apiLocale: TLocaleAPI): TSubscriptionEndpoints => subscriptions(api, apiLocale);
